import { get } from '@/api'
import { buildPrivileges } from '@/auth/authorization'

const USER_SEARCH_PAGE_SIZE = 25
// Keeps each OR query_expression URL well under any proxy limit.
const RESOLVE_CHUNK_SIZE = 50

function getBaseUserObject() {
  return {
    dateJoined: '',
    email: '',
    fullName: '',
    groups: [],
    id: -1,
    isActive: false,
    isStaff: false,
    isSuperUser: false,
    orcid: null,
    primaryGroup: { id: -1, name: '' },
    username: '',
  }
}

function normalizeGroup(group) {
  return { id: group?.id ?? -1, name: group?.name ?? '' }
}

/**
 * Maps a flat API user (`?response_format=flat`) or a decoded JWT payload — both
 * snake_case — onto the single camelCase shape used everywhere in the app.
 */
function normalizeUser(apiUser) {
  if (!apiUser || apiUser.id == null) return null
  return {
    dateJoined: apiUser.date_joined ?? '',
    email: apiUser.email ?? '',
    fullName: apiUser.full_name ?? '',
    groups: (apiUser.groups ?? []).map(normalizeGroup),
    id: Number(apiUser.id),
    isActive: Boolean(apiUser.is_active),
    isStaff: Boolean(apiUser.is_staff),
    isSuperUser: Boolean(apiUser.is_superuser),
    // The server serializes a missing profile as the literal string "None".
    orcid: !apiUser.orcid || apiUser.orcid === 'None' ? null : apiUser.orcid,
    primaryGroup: normalizeGroup(apiUser.primary_group),
    username: apiUser.username ?? '',
  }
}

function normalizeUsers(apiUsers) {
  return (apiUsers ?? []).map(normalizeUser).filter(Boolean)
}

function buildUsersUrl(params, queryExpression = null) {
  const searchParams = new URLSearchParams({
    response_format: 'flat',
    ...params,
  })
  if (queryExpression?.length) {
    // Must be encoded: search terms are free text, and a raw `&`, `#` or `+` in
    // one would otherwise break the query string apart.
    searchParams.set('query_operator', 'OR')
    searchParams.set('query_expression', `[${queryExpression.join(',')}]`)
  }
  return `users/?${searchParams.toString()}`
}

function usernameTerm(username) {
  return JSON.stringify({
    category: 'username',
    operator: 'exact',
    value: username,
  })
}

/**
 * Requests here must opt out of the global request manager. It cancels every
 * in-flight request on navigation, and a cancelled request resolves to a promise
 * that NEVER settles (see api/interceptors.js). A cancelled bootstrap would hang
 * app init forever; a cancelled user lookup would wedge its entry in
 * `inflightByUsername` for the rest of the session.
 */
const UNCANCELLABLE = { cancellable: false }

// A non-2xx response rejects in the api layer, so callers only ever see success
// here — no status check needed.
async function fetchUsersByUsernames(usernames) {
  const url = buildUsersUrl({ page_size: 'max' }, usernames.map(usernameTerm))
  const { data } = await get(url, UNCANCELLABLE)
  return normalizeUsers(data.results)
}

// In-flight requests are deduplicated at module scope: promises are not
// serializable and have no business living in Vuex state.
const inflightByUsername = new Map()
let inflightCurrentUser = null
let inflightAllUsers = null

function clearInflight() {
  inflightByUsername.clear()
  inflightCurrentUser = null
  inflightAllUsers = null
}

function getDefaultState() {
  return {
    loggedIn: false,
    showLoginOverlay: false,
    privileges: [],
    user: getBaseUserObject(),
    // id -> user
    usersById: {},
    // username -> id, or null when the server has no such user (negative cache,
    // so a deleted note author is not re-requested on every mount)
    usersByUsername: {},
    allUsersLoaded: false,
    isFetchingUsers: false,
  }
}

const state = getDefaultState()

const getters = {
  loggedInUser: (state) => {
    return state.user
  },
  loggedInUserGroupIds: (state) => {
    return state.user.groups.map((g) => g.id)
  },
  loggedInUserPrimaryGroup: (state) => {
    return state.user.primaryGroup
  },
  isLoggedIn: (state) => {
    return state.loggedIn === true
  },
  isLoggedInUser: (state, getters) => (user) => {
    if (!user) return false
    return (
      state.loggedIn === true &&
      ((user.id && user.id !== -1 && getters.isLoggedInUserById(user.id)) ||
        (user.username &&
          user.username !== '' &&
          getters.isLoggedInUserByUsername(user.username)))
    )
  },
  isLoggedInUserById: (state) => (id) => {
    return id && state.user.id && id === state.user.id
  },
  isLoggedInUserByUsername: (state) => (username) => {
    return username && state.user.username && username === state.user.username
  },
  // Returns undefined on a miss. Callers must handle "not resolved yet" and "no
  // such user" — an empty placeholder object would only hide them.
  getUserByUsername: (state) => (username) => {
    const id = state.usersByUsername[username]
    return id == null ? undefined : state.usersById[id]
  },
  // True once the server has answered for this username — whether or not such a
  // user exists. Lets callers tell "no such user" from "the request failed".
  isUserResolved: (state) => (username) => username in state.usersByUsername,
  // Partial until fetchUsers() resolves — check allUsersLoaded if you need completeness.
  // Sorted by username: usersById is keyed by id, and integer-like keys iterate in
  // numeric order, which would otherwise list users by signup order.
  allUsers: (state) => {
    return Object.values(state.usersById).sort((a, b) =>
      a.username.localeCompare(b.username),
    )
  },
  isUserReady: (state, getters) =>
    getters.isLoggedIn && state.user.username.length > 0,
  isRegularUser: (state) => !state.user.isSuperUser,
  isAdminUser: (state) => state.user.isSuperUser,
}

const actions = {
  reset({ commit }) {
    // Before RESET_STATE, so a request in flight across a logout cannot resolve
    // into the fresh store.
    clearInflight()
    commit('RESET_STATE', getDefaultState())
  },
  clearBackendCache({ state }) {
    return get(`annotations/metadata/${state.user.username}/clear`)
  },
  logoutUser({ dispatch }, clearBackendCache) {
    if (clearBackendCache === true) dispatch('clearBackendCache')
    dispatch('reset')
  },
  async loginUser({ commit, dispatch }, payload) {
    await dispatch('setUser', payload)
    commit('SET_LOGIN', true)
  },
  setUser({ commit }, tokenUser) {
    commit('SET_TOKEN_USER', tokenUser)
  },
  /**
   * The JWT carries no groups, so the current user's group info comes from here.
   * Throws on failure: `groups` / `primaryGroup` are load-bearing for the server
   * settings and the ownership filters, and a silently half-initialised session
   * is worse than a failed one.
   */
  async fetchCurrentUser({ commit }) {
    if (inflightCurrentUser) return inflightCurrentUser
    inflightCurrentUser = (async () => {
      const { data } = await get(
        'users/me/?response_format=flat',
        UNCANCELLABLE,
      )
      // `me` is a detail route: `results` is the user object, not a list.
      const user = normalizeUser(data?.results)
      if (!user) throw new Error('Cannot retrieve current user')
      commit('SET_CURRENT_USER', user)
      // Cache self, so your own profile page and your own notes cost no request.
      commit('UPSERT_USERS', [user])
      return user
    })().finally(() => {
      inflightCurrentUser = null
    })
    return inflightCurrentUser
  },
  /**
   * Resolves usernames to user objects in a single request, skipping any already
   * cached or already in flight. Never rejects.
   */
  async resolveUsers({ state, commit }, usernames = []) {
    const unresolved = [...new Set(usernames.filter(Boolean))].filter(
      (username) => !(username in state.usersByUsername),
    )
    if (!unresolved.length) return

    const pending = []
    const toFetch = []
    unresolved.forEach((username) => {
      const inflight = inflightByUsername.get(username)
      if (inflight) pending.push(inflight)
      else toFetch.push(username)
    })

    for (let i = 0; i < toFetch.length; i += RESOLVE_CHUNK_SIZE) {
      const chunk = toFetch.slice(i, i + RESOLVE_CHUNK_SIZE)
      const request = fetchUsersByUsernames(chunk)
        .then((users) => {
          commit('UPSERT_USERS', users)
          const found = new Set(users.map((u) => u.username))
          const missing = chunk.filter((username) => !found.has(username))
          if (missing.length) commit('SET_USERS_MISSING', missing)
        })
        .catch((error) => {
          // A transient failure must not be cached as "no such user", or the
          // name could never be resolved again for the life of the session.
          console.warn('Could not resolve users', chunk, error)
        })
        .finally(() => {
          chunk.forEach((username) => inflightByUsername.delete(username))
        })
      chunk.forEach((username) => inflightByUsername.set(username, request))
      pending.push(request)
    }
    await Promise.all(pending)
  },
  async resolveUser({ dispatch, getters }, username) {
    if (!username) return undefined
    await dispatch('resolveUsers', [username])
    return getters.getUserByUsername(username)
  },
  /**
   * Type-ahead backing for SelectContainer's `on-search`. Returns the axios
   * response with `data` as `{ results, total, total_pages }` — the contract the
   * component expects — but with `results` already normalized to camelCase, so
   * consumers never see the snake_case API shape.
   *
   * Matches `icontains` on full_name OR username rather than `?search=`, whose
   * search_fields ("^username", "^email", "$full_name") match username/email as
   * starts-with and run full_name as a regex — this keeps free-text user input
   * out of a server-side regex.
   */
  async searchUsers({ commit }, { search = '', signal } = {}) {
    const query = (search || '').trim()
    const url = buildUsersUrl(
      { page: '1', page_size: String(USER_SEARCH_PAGE_SIZE) },
      query
        ? ['full_name', 'username'].map((category) =>
            JSON.stringify({ category, operator: 'icontains', value: query }),
          )
        : null,
    )
    // The abort signal already cancels superseded keystrokes; opting out of the
    // request manager keeps a navigation from wedging the search in a loading state.
    const response = await get(url, {
      ...UNCANCELLABLE,
      ...(signal && { signal }),
    })
    if (response?.status !== 200) return response

    const results = normalizeUsers(response.data.results)
    commit('UPSERT_USERS', results)
    return { ...response, data: { ...response.data, results } }
  },
  /**
   * The whole user table. Only the superuser impersonation picker needs this, so
   * it is fetched lazily on demand rather than on the login path.
   */
  async fetchUsers({ state, commit }) {
    if (state.allUsersLoaded) return
    if (inflightAllUsers) return inflightAllUsers
    commit('SET_FETCHING_USERS', true)
    inflightAllUsers = get(buildUsersUrl({ page_size: 'max' }), UNCANCELLABLE)
      .then(({ data }) => {
        commit('UPSERT_USERS', normalizeUsers(data.results))
        commit('SET_ALL_USERS_LOADED', true)
      })
      .catch(() => {
        commit(
          'notify',
          { type: 'error', message: 'Could not load the list of users.' },
          { root: true },
        )
      })
      .finally(() => {
        commit('SET_FETCHING_USERS', false)
        inflightAllUsers = null
      })
    return inflightAllUsers
  },
  async fetchPrivileges({ dispatch }) {
    const response = await get('permissions/privileges/')
    const { status, data } = response || {}
    if (status === 200) {
      const { privileges: rPrivileges } = data
      const privileges = buildPrivileges(rPrivileges)
      return dispatch('setUsersPrivileges', privileges)
    }
  },
  setUsersPrivileges({ commit }, payload) {
    commit('SET_USERS_PRIVILEGES', payload)
  },
}

const mutations = {
  RESET_STATE(state, defaultState) {
    Object.assign(state, defaultState)
  },
  SET_LOGIN(state, isLoggedIn) {
    state.loggedIn = isLoggedIn
  },
  SET_TOKEN_USER(state, tokenUser) {
    // The token payload is snake_case like the API, but carries only these five
    // claims — the rest arrives with fetchCurrentUser().
    state.user.email = tokenUser.email
    state.user.fullName = tokenUser.full_name
    state.user.id = Number(tokenUser.id)
    state.user.isSuperUser = tokenUser.is_superuser
    state.user.username = tokenUser.username
  },
  SET_CURRENT_USER(state, user) {
    state.user = { ...state.user, ...user }
  },
  UPSERT_USERS(state, users) {
    users.forEach((user) => {
      state.usersById[user.id] = user
      state.usersByUsername[user.username] = user.id
    })
  },
  SET_USERS_MISSING(state, usernames) {
    usernames.forEach((username) => {
      state.usersByUsername[username] = null
    })
  },
  SET_ALL_USERS_LOADED(state, loaded) {
    state.allUsersLoaded = loaded
  },
  SET_FETCHING_USERS(state, isFetching) {
    state.isFetchingUsers = isFetching
  },
  SET_USERS_PRIVILEGES(state, privileges) {
    state.privileges = privileges
  },
  SHOW_LOGIN_OVERLAY(state, show) {
    show = show === undefined ? true : show
    state.showLoginOverlay = show
  },
}

const auth = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
}

export default auth
